import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Carrot,
  Apple,
  Milk,
  Beef,
  Wheat,
  Cookie,
  Soup,
  Sandwich,
  ShoppingCart,
} from "lucide-react";
import { getUserMealPlans } from "@/lib/mealPlan";
import { generateShoppingList } from "@/lib/shoppingList";
import { addProductsToCart } from './cartmaker';
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";

type CategoryIcon = {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  color: string;
};

type CategoryIcons = Record<string, CategoryIcon>;

const categoryIcons: CategoryIcons = {
  ירקות: { icon: Carrot, color: "text-orange-500" },
  פירות: { icon: Apple, color: "text-red-500" },
  "מוצרי חלב": { icon: Milk, color: "text-blue-400" },
  בשר: { icon: Beef, color: "text-red-600" },
  "בשר ודגים": { icon: Beef, color: "text-red-600" },
  "דגנים וקטניות": { icon: Wheat, color: "text-amber-600" },
  חטיפים: { icon: Cookie, color: "text-brown-500" },
  "מרקים ורטבים": { icon: Soup, color: "text-green-600" },
  "לחם ומאפים": { icon: Sandwich, color: "text-yellow-700" },
  "מוצרי לחם": { icon: Sandwich, color: "text-yellow-700" },
  "מוצרי יסוד": { icon: Wheat, color: "text-amber-600" },
  תבלינים: { icon: Soup, color: "text-green-700" },
  "שימורים ורטבים": { icon: Soup, color: "text-green-600" },
};

interface ServerMatch {
  item_name: string;
  price: number;
  item_code: string;
  total_price: number;
  similarity: number;
  word_match_ratio: number;
  final_score: number;
}

interface ServerItem {
  search_term: string;
  original_amount: number;
  original_unit: string;
  original_price: number;
  category: string;
  matches: ServerMatch[];
}

interface ServerResponse {
  status: string;
  parsed_items: number;
  total_price: number;
  result: ServerItem[];
}

export default function ShoppingList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serverData, setServerData] = useState<ServerResponse | null>(null);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [selectedMatches, setSelectedMatches] = useState<{
    [itemKey: string]: number;
  }>({});
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  useEffect(() => {
    const savedCheckedItems = localStorage.getItem("checkedShoppingItems");
    if (savedCheckedItems) {
      setCheckedItems(new Set(JSON.parse(savedCheckedItems)));
    }
    loadShoppingList();
  }, [location]);

  useEffect(() => {
    localStorage.setItem(
      "checkedShoppingItems",
      JSON.stringify([...checkedItems])
    );
  }, [checkedItems]);

  const loadShoppingList = async () => {
    try {
      setLoading(true);
      const mealPlanId = location.state?.mealPlanId;
      const plans = await getUserMealPlans();

      if (!plans || plans.length === 0) {
        throw new Error("No meal plans found");
      }

      const selectedPlan = mealPlanId
        ? plans.find((plan) => plan.id === mealPlanId)
        : plans[0];

      if (!selectedPlan) {
        throw new Error("Meal plan not found");
      }

      const response = await generateShoppingList(
        selectedPlan.plan_data,
        selectedPlan.id
      );
      if (!response.serverProcessing) {
        throw new Error("Invalid server response");
      }

      // First cast to unknown, then to ServerResponse
      const serverProcessing = response.serverProcessing as unknown as ServerResponse;
      if (!('parsed_items' in serverProcessing && 
          'total_price' in serverProcessing &&
          'result' in serverProcessing)) {
        throw new Error("Server response missing required properties");
      }

      setServerData(serverProcessing);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItemCheck = (itemKey: string) => {
    setCheckedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
  };

  const handleMatchChange = (itemKey: string, index: number) => {
    setSelectedMatches((prev) => ({
      ...prev,
      [itemKey]: index,
    }));
  };

  // Prepare data for adding to cart
  const prepareCartItems = () => {
    if (!serverData) return [];

    return serverData.result
      .map((item) => {
        const itemKey = `${item.search_term}-${item.original_unit}`;
        const selectedMatchIndex = selectedMatches[itemKey] ?? 0;
        const chosenMatch = item.matches[selectedMatchIndex];

        if (!chosenMatch) return null;

        return {
          // Add the "P_" prefix to the code
          code: `P_${chosenMatch.item_code}`,
          quantity: item.original_amount.toString(),
        };
      })
      .filter((item) => item !== null);
  };

  const handleAddToCart = async () => {
    try {
      setIsAddingToCart(true);
      
      // Get Shufersal credentials from the database
      const { data: credentials, error: credentialsError } = await supabase
        .from('user_shufersal_credentials')
        .select('shufersal_email, shufersal_password')
        .single();

      if (credentialsError || !credentials) {
        throw new Error('Could not fetch Shufersal credentials');
      }

      if (!serverData) {
        throw new Error('No shopping list data available');
      }

      // Format the items array
      const items = serverData.result
        .map((item) => {
          const itemKey = `${item.search_term}-${item.original_unit}`;
          const selectedMatchIndex = selectedMatches[itemKey] ?? 0;
          const chosenMatch = item.matches[selectedMatchIndex];

          if (!chosenMatch) return null;

          return {
            code: `P_${chosenMatch.item_code}`,
            quantity: item.original_amount.toString()
          };
        })
        .filter((item): item is { code: string; quantity: string } => item !== null);

      // Create the request body in the required format
      const requestBody = {
        username: credentials.shufersal_email,
        password: credentials.shufersal_password,
        items: items
      };

      // Send the request to the matching server
      try {
        const response = await addProductsToCart(requestBody);
        if (response.status === 'success') {
          toast({
            title: "הצלחה!",
            description: "המוצרים נוספו לעגלת הקניות בשופרסל",
            variant: "default",
          });
        } else {
          throw new Error('Failed to add products to cart');
        }
      } catch (error) {
        console.error('Failed to add items to cart:', error);
        toast({
          title: "שגיאה",
          description: "לא הצלחנו להוסיף את המוצרים לעגלת הקניות",
          variant: "destructive",
        });
      }

    } catch (error) {
      console.error('Failed to add items to cart:', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const itemsByCategory = useMemo(() => {
    if (!serverData) return {};
    const categoryMap: { [category: string]: ServerItem[] } = {};

    serverData.result.forEach((item) => {
      const cat = item.category || "אחר";
      if (!categoryMap[cat]) {
        categoryMap[cat] = [];
      }
      categoryMap[cat].push(item);
    });
    return categoryMap;
  }, [serverData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (!serverData) {
    return null;
  }

  const totalItems = serverData.result.length;
  const totalChecked = [...checkedItems].length;

  return (
    <div className="container mx-auto p-4 space-y-8">
      {/* Title & Info row */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <h1 className="text-xl font-bold">רשימת קניות</h1>
        <div className="text-sm text-gray-600">
          מסומנים: {totalChecked} / {totalItems}
        </div>
      </div>

      {/* Categories grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(itemsByCategory).map(([category, items]) => {
          const IconComponent = categoryIcons[category]?.icon || Carrot;
          const iconColor = categoryIcons[category]?.color || "text-gray-500";

          return (
            <Card key={category} className="p-4 space-y-4">
              <div className="flex items-center space-x-2 border-b pb-2">
                <IconComponent className={`w-5 h-5 ${iconColor}`} />
                <h2 className="text-lg font-semibold">{category}</h2>
              </div>
              <div className="space-y-3">
                {items.map((item) => {
                  // itemKey is used for checkbox + tracking selection
                  const itemKey = `${item.search_term}-${item.original_unit}`;

                  // If user picked a different match from the dropdown, show that, otherwise fallback to first match
                  const selectedMatchIndex = selectedMatches[itemKey] ?? 0;
                  const chosenMatch = item.matches[selectedMatchIndex] || item.matches[0];

                  return (
                    <div key={itemKey} className="flex flex-col space-y-1">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          checked={checkedItems.has(itemKey)}
                          onCheckedChange={() => handleToggleItemCheck(itemKey)}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between w-full">
                            <span
                              className={
                                checkedItems.has(itemKey) ? "line-through" : ""
                              }
                            >
                              {item.search_term}
                            </span>
                            {chosenMatch && (
                              <span className="ml-4 text-sm text-gray-600">
                                ₪{chosenMatch.price.toFixed(2)}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {item.original_amount} {item.original_unit}
                          </div>
                        </div>
                      </div>
                      {/* Dropdown for alternatives */}
                      {item.matches && item.matches.length > 1 && (
                        <div className="ml-7 text-sm text-gray-500">
                          <label className="mr-2">בחר חלופה:</label>
                          <select
                            className="rounded border border-gray-300 px-2 py-1"
                            value={selectedMatchIndex}
                            onChange={(e) =>
                              handleMatchChange(itemKey, parseInt(e.target.value))
                            }
                          >
                            {item.matches.map((match, idx) => (
                              <option key={match.item_code} value={idx}>
                                {match.item_name} | ₪{match.price.toFixed(2)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {/* If only one match, we won't show dropdown. */}
                      {/* Show additional info about currently chosen match */}
                      {chosenMatch && (
                        <div className="ml-7 text-xs text-gray-400">
                          ברקוד: {chosenMatch.item_code}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Footer with totals and add to cart button */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 space-y-4">
        <Card className="p-4 bg-primary text-primary-foreground">
          <div className="flex items-center justify-between">
            <span className="font-semibold">סה"כ משוער:</span>
            <span className="text-xl">
              ₪{serverData?.total_price.toFixed(2)}
            </span>
          </div>
        </Card>

        <Button
          className="w-full"
          onClick={handleAddToCart}
          disabled={isAddingToCart}
        >
          {isAddingToCart ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShoppingCart className="mr-2 h-4 w-4" />
          )}
          הוסף לעגלת הקניות
        </Button>
      </div>
    </div>
  );
}
